import { useContext, useEffect, useRef, useState, Suspense, lazy, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Box, Dialog, CircularProgress, Drawer } from '@mui/material'

// Lazy load DayViewCalendarComponent - it's a large component (3,672 lines) with many dependencies
const DayViewCalendarComponent = lazy(() =>
  import('src/clinic/components/ClinicManageBookingsPage/DayViewCalendarComponent')
)
import { PastTherapyReportDialogPaper } from 'src/clinic/components/ClinicManageBookingsPage/receiptPrinting/styledComponents'
import NewBookingPopup from 'src/mentor/components/MentorClinicBookingsPage/component/newBooking/NewBookingPopup'
import { fetchDrugAdviceEntity } from 'src/clinic/reducers/prescription/services/drugAdviceItems'
import {
  DISPATCH_EVENT_KEYS,
  HE_DRUG_ADVICE_DOSAGE,
  HE_DRUG_ADVICE_DURATION,
  HE_DRUG_ADVICE_FREQUENCY,
  HE_DRUG_ADVICE_NOTE,
  HE_DRUG_ADVICE_TIMING,
  HE_DRUG_ADVICE_TYPE,
} from 'src/consumer/constants'
import useElementDimensions, { useScreenSize } from 'src/customHooks/customHooks'
import ClinicLocationCheckInGate from 'src/mentor/components/MentorClinicBookingsPage/component/ClinicLocationCheckInGate'
import {
  clinicLocationAccessEquals,
  deriveClinicLocationAccess,
  findClinicAssociationByUrlKey,
  getClinicCalendarPath,
  looksLikeClinicLocationId,
  readStoredCheckInAccessRaw,
} from 'src/mentor/components/MentorClinicBookingsPage/clinicLocationAccess'
import HeaderItems from 'src/mentor/components/MentorClinicBookingsPage/component/HeaderItems'
import PatientSearchDrawer from 'src/mentor/components/MentorClinicBookingsPage/component/PatientSearchDrawer'
import ClinicBookingsLeftMenu from 'src/mentor/components/MentorClinicBookingsPage/component/ClinicBookingsLeftMenu'
import ClinicReportsWithVisualizations from 'src/mentor/components/MentorClinicBookingsPage/component/ClinicReportsWithVisualizations'
import ClinicStatsView from 'src/mentor/components/MentorClinicBookingsPage/component/ClinicStatsView'
import MentorRosterView from 'src/mentor/components/MentorClinicBookingsPage/component/roster/MentorRosterView'
import {
  clearPortalVoiceLauncherContext,
  publishPortalVoiceLauncherContext,
} from 'src/mentor/components/voice/portalVoiceLauncherContext'
import { selectAppointmentCalendarSettings } from 'src/clinic/reducers/prescription/prescriptionReducer'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { MentorClinicsContext } from 'src/mentor/layout'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { getDoctorRecommendedHealthEntities } from 'src/reducers/doctorRecommendedHealthEntities/services/doctorRecommendedHealthEntities'
import { getScreeningReportTemplateData } from 'src/reducers/screeningReportTemplate/services/screeningReportTemplate'
import { fetchEmrSections } from 'src/clinic/reducers/prescription/services/emrSectionThunk'
import { fetchPrescriptionViewSettings } from 'src/clinic/reducers/prescription/services/prescriptionViewSettingsThunk'
import {
  AvailabilityPageContainer,
  PageHeadlineComponent,
  PageMainContentContainer,
} from '../MentorAvailabilityPage/MentorAvailabilityPageStyles'
import { MENTOR_CLINIC_BOOKING_TABS, MENTOR_CLINIC_BOOKING_TABS_LIST } from './util'
import MedicalCertificateProvider from 'src/mentor/components/MedicalCertificate/MedicalCertificateProvider'

// Width of the desktop left-menu rail (fixed, slim icon+label rail). The content column is offset
// by the same amount.
const RAIL_WIDTH = 92

// Fired by the left-menu "Help & Calls" button; the global FloatingIntercomButton listens for it.
const OPEN_INTERCOM_EVENT = 'aumhum:open-intercom'
// Fired by the left-menu "DocSide" button (mobile, where the floating DocSide tab is hidden); the
// global YoutubeHelper/DocSide listens for it.
const OPEN_DOCSIDE_EVENT = 'aumhum:open-docside'

export default function MentorClinicBookingsPage() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  // Two route shapes land here: the legacy /my-clinic-associations/:clinicBrandId/:clinicId
  // (ids) and the short /my-clinic-associations/:clinicKey (location slug, or an id as a
  // fallback). Either key is resolved against the loaded associations; the rest of the page
  // always works with canonical ids so children and API calls are unaffected by URL form.
  const {
    clinicId: clinicIdParam,
    clinicBrandId: clinicBrandIdParam,
    clinicKey: clinicKeyParam,
  } = useParams()
  const {
    mentorProfileData,
    mentorClinicAssociationData,
    mentorChamberRoomsData,
    portalViewSettings,
  } =
    useContext(MentorProfileContext)

  const requestedClinicKey = clinicIdParam || clinicKeyParam
  const thisClinicData = useMemo(
    () => findClinicAssociationByUrlKey(mentorClinicAssociationData, requestedClinicKey),
    [mentorClinicAssociationData, requestedClinicKey],
  )
  const clinicId =
    thisClinicData?.id ||
    clinicIdParam ||
    (looksLikeClinicLocationId(requestedClinicKey) ? requestedClinicKey : undefined)
  const clinicBrandId = thisClinicData?.clinicBrandId || clinicBrandIdParam

  const hidePatientContactDetails = Boolean(portalViewSettings?.hidePatientContactDetails)

  // Left-menu items reuse this; also drives the role-based default tab below.
  const allRoles = useMemo(() => getAllRolesInBoolean(), [])

  // Warm the doctor-wide EMR config (sections, print/view settings, most-used suggestions) while
  // the practitioner looks at their appointment list, so opening the first session (/l/*) paints
  // the editor from a warm Redux cache instead of waiting on these fetches. All three thunks are
  // cached + in-flight-deduped, so firing them here is cheap and idempotent.
  useEffect(() => {
    const isPractitioner = Boolean(
      allRoles?.isDoctor || allRoles?.isTeacher || allRoles?.isTherapist || allRoles?.isScreener,
    )
    if (!isPractitioner) {
      return
    }
    dispatch(fetchEmrSections())
    dispatch(fetchPrescriptionViewSettings({ emrReportType: 'prescription' }))
    if (clinicBrandId) {
      dispatch(getDoctorRecommendedHealthEntities({ clinicBrandId }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicBrandId])

  // The active tab is mirrored into the URL (?tab=appointments) so that navigating
  // to an event page (/l/*) and back restores whichever tab the user came from.
  const [searchParams, setSearchParams] = useSearchParams()
  // Default listing tab, used when the URL carries no explicit ?tab=. Resolution order:
  // 1. The practitioner's backend setting (Appointment Calendar Settings → default tab),
  //    editable in the mentor settings page and by support on the practitioner's behalf.
  // 2. Doctors land on Appointments (their working day list).
  // 3. Everyone else keeps the device default: Appointments on a phone, Calendar on desktop.
  const configuredDefaultTab = useSelector(selectAppointmentCalendarSettings)?.data
    ?.defaultClinicAssociationsTab
  const getDefaultTabId = useCallback(() => {
    if (
      configuredDefaultTab &&
      MENTOR_CLINIC_BOOKING_TABS_LIST.some((tab) => tab?.id === configuredDefaultTab)
    ) {
      return configuredDefaultTab
    }
    if (allRoles?.isDoctor) return MENTOR_CLINIC_BOOKING_TABS.appointments.id
    return typeof window !== 'undefined' && window.innerWidth < 768
      ? MENTOR_CLINIC_BOOKING_TABS.appointments.id
      : MENTOR_CLINIC_BOOKING_TABS.calendar.id
  }, [configuredDefaultTab, allRoles])
  const getTabFromUrl = useCallback(() => {
    const tabParam = searchParams.get('tab')
    return MENTOR_CLINIC_BOOKING_TABS_LIST.some((tab) => tab?.id === tabParam)
      ? tabParam
      : getDefaultTabId()
  }, [searchParams, getDefaultTabId])
  const [selectedTab, setSelectedTabState] = useState(getTabFromUrl)
  const setSelectedTab = useCallback(
    (tabId) => {
      setSelectedTabState(tabId)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (tabId === getDefaultTabId()) {
            next.delete('tab') // the resolved default — keep the URL clean
          } else {
            next.set('tab', tabId)
          }
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams, getDefaultTabId],
  )
  // Keep the active tab in sync when the URL changes outside of a tab click
  // (browser back/forward, or returning from a live-session page with ?tab=...).
  useEffect(() => {
    const tabFromUrl = getTabFromUrl()
    setSelectedTabState((prev) => (prev === tabFromUrl ? prev : tabFromUrl))
  }, [getTabFromUrl])

  const [isPatientSearchOpen, setIsPatientSearchOpen] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [navOpen, setNavOpen] = useState(false)
  // True while the New-booking dialog is open. We make the fixed header non-interactive then, so a
  // tap on the dialog's close "✕" (which overlaps the header's Checkout button) can't fall through
  // to Checkout and accidentally check the user out.
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false)
  const dayViewCalendarRef = useRef(null)

  useEffect(() => {
    const onToggle = (e) => setBookingDialogOpen(Boolean(e?.detail?.open))
    window.addEventListener('aumhum:booking-dialog-toggle', onToggle)
    return () => window.removeEventListener('aumhum:booking-dialog-toggle', onToggle)
  }, [])

  // Left-menu items: same role filtering used by the (now hidden) header tabs, plus the new
  // full-page Patients entry that lives only in the menu (see util.js patientsPage).
  const menuItems = useMemo(
    () =>
      MENTOR_CLINIC_BOOKING_TABS_LIST.filter((tab) => {
        if (allRoles.isDoctor) return tab?.roleType?.includes('ROLE_DOCTOR')
        if (allRoles.isClinicAssistant) return tab?.roleType?.includes('ROLE_CLINIC_ASSISTANT')
        return tab?.roleType?.includes('ROLE_THERAPIST')
      }),
    [allRoles],
  )
  const isPatientsPageView = selectedTab === MENTOR_CLINIC_BOOKING_TABS.patientsPage.id
  const isStatsPageView = selectedTab === MENTOR_CLINIC_BOOKING_TABS.statsPage.id
  const isRosterView = selectedTab === MENTOR_CLINIC_BOOKING_TABS.roster.id
  // Patient search/add only makes sense where you book against a patient (Calendar + the day's
  // Appointments list); Planned, Roster, Stats and the Patients page surface their own context.
  const isSearchContextTab =
    selectedTab === MENTOR_CLINIC_BOOKING_TABS.calendar.id ||
    selectedTab === MENTOR_CLINIC_BOOKING_TABS.appointments.id
  // The unified day navigator (date + calendar dropdown) applies to every date-driven view —
  // Calendar, Appointments AND Planned — so the date picker is the same everywhere.
  const isDateContextTab =
    isSearchContextTab || selectedTab === MENTOR_CLINIC_BOOKING_TABS.planned.id
  // Show it in the main header when the viewport is wide enough to hold it alongside the identity
  // card + actions; otherwise the calendar's own floating header keeps showing the date picker.
  const { width: screenWidth } = useScreenSize()
  const showDatePickerInMainHeader = isDateContextTab && screenWidth >= 1280
  const handleMenuSelect = useCallback(
    (tabId) => {
      setSelectedTab(tabId)
      setNavOpen(false)
    },
    [setSelectedTab],
  )
  const handleOpenIntercom = useCallback(() => {
    setNavOpen(false)
    window.dispatchEvent(new CustomEvent(OPEN_INTERCOM_EVENT))
  }, [])
  const handleOpenDocSide = useCallback(() => {
    setNavOpen(false)
    window.dispatchEvent(new CustomEvent(OPEN_DOCSIDE_EVENT))
  }, [])

  // Horizontal swipe between the two listing tabs (Appointments ⇄ Calendar) on touch devices.
  const swipeStartRef = useRef(null)
  const handleContentTouchStart = useCallback((e) => {
    const t = e.touches?.[0]
    swipeStartRef.current = t ? { x: t.clientX, y: t.clientY } : null
  }, [])
  const handleContentTouchEnd = useCallback(
    (e) => {
      const start = swipeStartRef.current
      swipeStartRef.current = null
      const t = e.changedTouches?.[0]
      if (!start || !t) return
      const dx = t.clientX - start.x
      const dy = t.clientY - start.y
      // Only a deliberate, mostly-horizontal swipe counts (so vertical scrolls/taps are ignored).
      if (Math.abs(dx) < 70 || Math.abs(dx) < Math.abs(dy) * 1.6) return
      const isAppts = selectedTab === MENTOR_CLINIC_BOOKING_TABS.appointments.id
      const isCal = selectedTab === MENTOR_CLINIC_BOOKING_TABS.calendar.id
      if (!isAppts && !isCal) return
      if (dx < 0 && isAppts) setSelectedTab(MENTOR_CLINIC_BOOKING_TABS.calendar.id)
      else if (dx > 0 && isCal) setSelectedTab(MENTOR_CLINIC_BOOKING_TABS.appointments.id)
    },
    [selectedTab, setSelectedTab],
  )

  const clinicsContext = useContext(MentorClinicsContext) || {}
  const { clinicEventTypesMap } = clinicsContext
  const currentClinicEvents = clinicEventTypesMap?.[clinicId]

  const { ref: headerRef, dimensions } = useElementDimensions()
  const headerHeight = dimensions?.height || 80

  // Location-scoped check-in/peek-in access, derived from the same localStorage keys the
  // check-in flows maintain. Drives the full-screen check-in gate below, which replaced
  // the old redirect back to /mentor-home/my-clinic-associations.
  const [clinicAccess, setClinicAccess] = useState(() =>
    deriveClinicLocationAccess(readStoredCheckInAccessRaw(), clinicId),
  )
  useEffect(() => {
    const refreshClinicAccess = () => {
      setClinicAccess((previousAccess) => {
        const nextAccess = deriveClinicLocationAccess(readStoredCheckInAccessRaw(), clinicId)
        return clinicLocationAccessEquals(previousAccess, nextAccess) ? previousAccess : nextAccess
      })
    }
    refreshClinicAccess()
    const intervalId = setInterval(refreshClinicAccess, 1000)
    window.addEventListener('storage', refreshClinicAccess)
    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, refreshClinicAccess)
    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedOut, refreshClinicAccess)
    return () => {
      clearInterval(intervalId)
      window.removeEventListener('storage', refreshClinicAccess)
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, refreshClinicAccess)
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedOut, refreshClinicAccess)
    }
  }, [clinicId])

  // The blocking gate: shown when the practitioner has neither an active check-in nor an
  // active peek-in at THIS clinic location. Waits for the association to resolve (so a
  // slug URL never flashes the gate while the clinic list loads) and ignores an access
  // state computed for a stale location during route transitions. The calendar keeps
  // loading underneath so the day is ready the moment they check in.
  const [manualGateOpen, setManualGateOpen] = useState(false)
  const showCheckInGate = Boolean(
    thisClinicData &&
      clinicId &&
      clinicAccess.clinicLocationId === clinicId &&
      !clinicAccess.isCheckedInHere &&
      !clinicAccess.isPeekedInHere,
  )
  useEffect(() => {
    if (clinicAccess.isCheckedInHere) {
      setManualGateOpen(false)
    }
  }, [clinicAccess.isCheckedInHere])

  // Load non-critical prescription data after initial render
  // These are only needed when creating/editing prescriptions, not for calendar view
  useEffect(() => {
    console.log({ mentorClinicAssociationData, clinicId })

    if (
      mentorClinicAssociationData &&
      mentorClinicAssociationData?.length > 0 &&
      mentorClinicAssociationData.some((clinic) => clinic?.id === clinicId)
    ) {
      if (getAllRolesInBoolean().isDoctor) {
        // Defer non-critical data fetching to improve initial page load
        // Use requestIdleCallback when available, otherwise setTimeout
        const loadNonCriticalData = () => {
          // Screening report template - only needed when creating screening reports
          dispatch(getScreeningReportTemplateData({ clinicBrandId }))
          
          // Doctor recommended health entities - only needed when creating prescriptions
          dispatch(getDoctorRecommendedHealthEntities({ clinicBrandId }))
          
          // Drug advice entities - only needed when creating prescriptions
          // These can be loaded in parallel
          dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_TYPE }))
          dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_TIMING }))
          dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_FREQUENCY }))
          dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_DURATION }))
          dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_NOTE }))
          dispatch(fetchDrugAdviceEntity({ clinicBrandId, entity: HE_DRUG_ADVICE_DOSAGE }))
        }

        // Use requestIdleCallback if available (loads when browser is idle)
        // Falls back to setTimeout for older browsers
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(loadNonCriticalData, { timeout: 2000 })
        } else {
          // Fallback: delay by 500ms to allow initial render to complete
          setTimeout(loadNonCriticalData, 500)
        }
      }
    } else {
      console.log('TRIED TO NAVIGATE TO MENTOR HOME')
      // navigate('/mentor-home')
    }
  }, [])

  // Canonicalize the URL to the short slug form once the association resolves. Id URLs
  // keep working — they're just upgraded in place, preserving the query string (?tab=…).
  useEffect(() => {
    const canonicalSlug =
      typeof thisClinicData?.slug === 'string' ? thisClinicData.slug.trim() : ''
    if (!canonicalSlug || requestedClinicKey === canonicalSlug) {
      return
    }
    const searchSuffix = searchParams.toString()
    navigate(`${getClinicCalendarPath(thisClinicData)}${searchSuffix ? `?${searchSuffix}` : ''}`, {
      replace: true,
    })
  }, [navigate, requestedClinicKey, searchParams, thisClinicData])

  // A URL key that isn't one of the practitioner's clinics (stale bookmark, typo'd slug)
  // still bounces to the clinic list — the check-in gate can only offer rooms for clinics
  // the practitioner is actually associated with.
  useEffect(() => {
    if (
      Array.isArray(mentorClinicAssociationData) &&
      mentorClinicAssociationData.length > 0 &&
      requestedClinicKey &&
      !thisClinicData
    ) {
      navigate('/mentor-home/my-clinic-associations', { replace: true })
    }
  }, [mentorClinicAssociationData, navigate, requestedClinicKey, thisClinicData])

  const refreshData = useCallback(async () => {
    if (!dayViewCalendarRef.current) {
      return
    }
    await dayViewCalendarRef.current?.getAndSetBookedSlotsOfSelectedDate()
  }, [])

  // Opens the "Search or Create Patient" booking dialog (same one the Calendar
  // tab opens on an empty-slot click) for the Appointments tab "New" button.
  const handleOpenNewBooking = useCallback(() => {
    dayViewCalendarRef.current?.openNewBookingDialog?.()
  }, [])

  useEffect(() => {
    const launcherContextSourceKey = `mentor-clinic-bookings:${clinicBrandId || 'none'}:${clinicId || 'none'}`

    if (!clinicBrandId || !clinicId) {
      clearPortalVoiceLauncherContext(launcherContextSourceKey)
      return
    }

    publishPortalVoiceLauncherContext({
      actorRole: 'mentor',
      clinicBrandId,
      clinicId,
      uiSurface: 'mentor-floating-launcher',
      allowedDestinationTypes: ['support-team', 'clinic-assistant'],
    }, launcherContextSourceKey)

    return () => {
      clearPortalVoiceLauncherContext(launcherContextSourceKey)
    }
  }, [clinicBrandId, clinicId])

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient)
    // Patient is selected, close drawer and proceed with booking
  }

  // Prevent scrolling above the fixed header and ensure page starts at top
  useEffect(() => {
    // Track last scroll position to detect programmatic scrolling
    let lastScrollY = window.scrollY
    let scrollChangeTimeout = null
    let isAutoScrolling = false
    
    // Ensure page starts at top immediately
    const resetScroll = () => {
      // Don't reset if auto-scroll is in progress
      if (isAutoScrolling) {
        return
      }
      if (window.scrollY < 0 || document.documentElement.scrollTop < 0 || document.body.scrollTop < 0) {
        window.scrollTo(0, 0)
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
      }
    }
    
    // Reset on mount immediately (before auto-scroll)
    resetScroll()
    
    // Delay subsequent resets to allow auto-scroll to complete
    // Auto-scroll happens at ~1000ms, so delay our resets until after that
    const timeoutId = setTimeout(() => {
      resetScroll()
    }, 1500) // After auto-scroll completes
    const timeoutId2 = setTimeout(() => {
      resetScroll()
    }, 2000) // Additional safety check
    
    const isInsideDropdownMenu = (element) => {
      if (!element || typeof element.closest !== 'function') {
        return false
      }
      // Check for Material-UI Menu components
      const muiMenu = element.closest('[class*="MuiMenu-root"]') ||
                      element.closest('[class*="MuiMenu-paper"]') ||
                      element.closest('[class*="MuiMenu-list"]') ||
                      element.closest('[class*="MuiMenuList-root"]') ||
                      element.closest('[class*="MuiMenuItem-root"]') ||
                      element.closest('[class*="MuiPaper-root"][role="presentation"]')
      // Check for react-select menus
      const reactSelectMenu = element.closest('[class*="menu"]') || 
                              element.closest('[id*="react-select"]') ||
                              element.closest('[class*="MenuList"]') ||
                              element.closest('[class*="menu-list"]') ||
                              element.closest('[class*="react-select"]')
      return muiMenu || reactSelectMenu
    }
    
    const isDrawerOpen = () => {
      // Check if the search drawer is open by looking for the MUI drawer element
      // Check for drawer with aria-hidden="false" or drawer paper element
      const drawer = document.querySelector('[class*="MuiDrawer-root"][aria-hidden="false"], [class*="MuiDrawer-paper"][aria-hidden="false"]')
      return drawer !== null
    }
    
    const handleScroll = (e) => {
      // If drawer is open, don't interfere with scroll events
      if (isDrawerOpen()) {
        return
      }
      
      // Check if there's an active dropdown menu open (Material-UI Menu or react-select)
      const activeDropdown = document.querySelector(
        '[class*="MuiMenu-root"][aria-hidden="false"], ' +
        '[class*="MuiMenu-paper"][aria-hidden="false"], ' +
        '[class*="menu"][class*="is-open"], ' +
        '[id*="react-select"][aria-expanded="true"]'
      )
      
      // If dropdown is open, allow all scrolling
      if (activeDropdown) {
        return
      }
      
      // Detect rapid scroll changes (indicating programmatic scroll like scrollIntoView)
      const currentScrollY = window.scrollY
      const scrollDelta = Math.abs(currentScrollY - lastScrollY)
      lastScrollY = currentScrollY
      
      // If scroll changed significantly (>50px), it might be programmatic scrolling
      if (scrollDelta > 50) {
        isAutoScrolling = true
        // Clear existing timeout
        if (scrollChangeTimeout) {
          clearTimeout(scrollChangeTimeout)
        }
        // Reset flag after scroll completes (smooth scrolls take ~500-1000ms)
        scrollChangeTimeout = setTimeout(() => {
          isAutoScrolling = false
        }, 1500)
      }
      
      // Only reset negative scrolling (scrolling above 0) - use requestAnimationFrame to avoid interfering
      // with normal scroll events that the floating header needs
      const scrollY = window.scrollY
      const docScrollTop = document.documentElement.scrollTop
      const bodyScrollTop = document.body.scrollTop
      
      // Only reset if scroll is actually negative (not just 0) and not auto-scrolling
      if (!isAutoScrolling && (scrollY < 0 || docScrollTop < 0 || bodyScrollTop < 0)) {
        // Use requestAnimationFrame to reset on next frame, allowing scroll events to propagate first
        requestAnimationFrame(() => {
          resetScroll()
        })
      }
    }
    
    const handleWheel = (e) => {
      // If drawer is open, don't interfere with wheel events
      if (isDrawerOpen()) {
        return
      }
      
      // Check if the wheel event is inside a dropdown menu (Material-UI Menu or react-select)
      const target = e.target
      if (isInsideDropdownMenu(target)) {
        // Allow scrolling inside dropdowns - don't prevent default
        return
      }
      
      // Check if there's an active Material-UI Menu open
      const activeMenu = document.querySelector('[class*="MuiMenu-root"][aria-hidden="false"]')
      if (activeMenu && activeMenu.contains(target)) {
        // Allow scrolling inside Material-UI Menu
        return
      }
      
      // Only prevent scrolling up when actually at the very top of the page
      // Check all scroll positions to be accurate
      const scrollY = window.scrollY
      const docScrollTop = document.documentElement.scrollTop
      const bodyScrollTop = document.body.scrollTop
      const scrollTop = scrollY || docScrollTop || bodyScrollTop
      
      // Only prevent if we're at or above the top (<= 0) AND trying to scroll up
      // Allow all downward scrolling (deltaY > 0) and all upward scrolling when scrollY > 0
      const isAtTop = scrollTop <= 0
      const isScrollingUp = e.deltaY < 0
      
      // Very strict: only prevent when exactly at top (0 or negative) AND scrolling up
      // This allows normal upward scrolling when not at the top
      if (isAtTop && isScrollingUp) {
        // Only prevent if we're truly at the top - allow any scrolling when scrollY > 0
        e.preventDefault()
        e.stopPropagation()
        return false
      }
      
      // Allow all other scrolling to proceed normally
    }
    
    // Use passive listeners for scroll (better performance) - allows floating header to detect scroll
    // Passive listeners don't block scrolling and allow the floating header to work properly
    window.addEventListener('scroll', handleScroll, { passive: true })
    document.addEventListener('scroll', handleScroll, { passive: true })
    // Use bubble phase (not capture) to allow normal scroll behavior first
    // Only prevent when we're actually at the top and scrolling up
    window.addEventListener('wheel', handleWheel, { passive: false })
    document.addEventListener('wheel', handleWheel, { passive: false })
    
    // Removed requestAnimationFrame loop - it was interfering with normal scrolling
    // The scroll event handler is sufficient to catch and reset negative scrolling
    
    return () => {
      clearTimeout(timeoutId)
      clearTimeout(timeoutId2)
      if (scrollChangeTimeout) {
        clearTimeout(scrollChangeTimeout)
      }
      window.removeEventListener('scroll', handleScroll)
      document.removeEventListener('scroll', handleScroll)
      window.removeEventListener('wheel', handleWheel)
      document.removeEventListener('wheel', handleWheel)
    }
  }, [])

  return (
    <MedicalCertificateProvider
      clinicId={clinicId}
      clinicBrandId={clinicBrandId}
      clinicName={thisClinicData?.name}
    >
    <AvailabilityPageContainer
      style={{
        marginTop: 0, 
        paddingTop: 0, 
        margin: 0, 
        padding: 0,
        position: 'relative',
        top: 0,
        left: 0,
        overflowY: 'visible',
        overflowX: 'hidden',
        minHeight: '100vh',
      }}
    >
      <PageHeadlineComponent
        fullWidthHeader
        noPadding
        pageContainerCustomStyles={{
          padding: 0,
          margin: 0,
          marginTop: 0,
          marginBottom: '0',
          position: 'fixed',
          top: '0px',
          left: '0px',
          right: '0px',
          width: '100%',
          zIndex: '15',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          // While the New-booking dialog is open, let taps in the header region pass through to the
          // dialog beneath (its close "✕" overlaps the Checkout button) instead of hitting Checkout.
          pointerEvents: bookingDialogOpen ? 'none' : 'auto',
        }}
        pageHeadlineCustomStyles={{ padding: 0, margin: 0 }}
        pageContainerRef={headerRef}
        pageContainerDataAttributes={{ 'data-portal-header': 'true' }}
        heading={
          <HeaderItems
            clinicBrandId={clinicBrandId}
            clinicId={clinicId}
            clinicAssociationData={mentorClinicAssociationData}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            mentorProfileData={mentorProfileData}
            refreshData={refreshData}
            // Refresh now lives as an explicit icon in the filter strip (second header row), so the
            // main row never renders its own.
            hideRefreshButton
            hideTabs
            showPatientSearchInHeader={isSearchContextTab}
            showClinicExitCard
            onOpenNav={() => setNavOpen(true)}
            onOpenPatientSearch={() => setIsPatientSearchOpen(true)}
            onOpenNewBooking={handleOpenNewBooking}
            // The header's "Check In" / "Peeking Privately" chip opens the same
            // screen-saver-style check-in screen the gate uses (instead of the legacy
            // check-in dialog), so both entry points share one flow on this page.
            onOpenCheckInGate={() => setManualGateOpen(true)}
            showNewBookingButton={selectedTab === MENTOR_CLINIC_BOOKING_TABS.appointments.id}
            showDateNavigator={showDatePickerInMainHeader}
            dateNavigatorClinicSchedules={thisClinicData?.schedules}
            showFilterStrip={isDateContextTab}
          />
        }
      />

      <PageMainContentContainer
        noMaxWidth
        style={{
          margin: '0',
          marginTop: '0',
          paddingTop: '0',
          padding: '0',
          height: selectedTab === MENTOR_CLINIC_BOOKING_TABS.roster.id ? '100%' : 'auto',
          minHeight: '0',
        }}
      >
        <Box
          sx={{
            height: `${headerHeight}px`,
            flexShrink: 0,
            margin: 0,
            padding: 0,
            minHeight: `${headerHeight}px`,
            maxHeight: `${headerHeight}px`,
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
            ...(isRosterView ? { height: '100%' } : {}),
          }}
        >
          {/* Desktop left-menu rail (mobile uses the drawer below).
              Fixed to the viewport so it stays put regardless of the ancestor overflow context
              (PageMainContentContainer's overflow-x:hidden would make a sticky child unreliable). */}
          <Box
            component="aside"
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'fixed',
              top: `${headerHeight}px`,
              left: 0,
              width: RAIL_WIDTH,
              height: `calc(100vh - ${headerHeight}px)`,
              overflowY: 'auto',
              padding: '12px 6px',
              borderRight: '1px solid rgba(16, 185, 129, 0.12)',
              backgroundColor: 'rgba(248, 250, 252, 0.6)',
              zIndex: 12,
            }}
          >
            <ClinicBookingsLeftMenu
              items={menuItems}
              selectedId={selectedTab}
              onSelect={handleMenuSelect}
              onOpenIntercom={handleOpenIntercom}
            />
          </Box>

          {/* Content column (offset on desktop to clear the fixed rail) */}
          <Box
            component="main"
            onTouchStart={handleContentTouchStart}
            onTouchEnd={handleContentTouchEnd}
            sx={{
              flex: 1,
              minWidth: 0,
              ml: { xs: 0, md: `${RAIL_WIDTH}px` },
              ...(isRosterView ? { height: '100%' } : {}),
            }}
          >
            {isPatientsPageView ? (
              <Box sx={{ height: `calc(100vh - ${headerHeight}px)`, padding: { xs: '8px', md: '16px' } }}>
                <PatientSearchDrawer
                  variant="page"
                  showClinicalTabs
                  open
                  onClose={() => {}}
                  onSelectPatient={handlePatientSelect}
                  onBookAppointment={(patient) => setSelectedPatient(patient)}
                  clinicId={clinicId}
                  clinicBrandId={clinicBrandId}
                  mentorProfileData={mentorProfileData}
                  searchPatientApiSignature="/api/secure/mentor/patient/list"
                  createPatientApiSignature="/api/secure/patient/new"
                  patientTagsApiSignature="/api/secure/teacher/patient/tags"
                  canViewPatientHistory={true}
                  patientHistoryGetApiSignature="/api/secure/patient/reports/list"
                />
              </Box>
            ) : isStatsPageView ? (
              <Box sx={{ minHeight: `calc(100vh - ${headerHeight}px)`, padding: { xs: '8px', md: '16px' } }}>
                <ClinicStatsView
                  allRoles={allRoles}
                  clinicBrandId={clinicBrandId}
                  clinicLocationId={clinicId}
                  mentorProfileData={mentorProfileData}
                  stickyTop={headerHeight}
                  ownerStats={
                    <Box sx={{ padding: { xs: '4px', md: '8px' } }}>
                      <ClinicReportsWithVisualizations
                        lockedClinicBrandId={clinicBrandId}
                        clinicLocationIdFilter={clinicId}
                      />
                    </Box>
                  }
                />
              </Box>
            ) : isRosterView ? (
              <Box sx={{ height: `calc(100vh - ${headerHeight}px)` }}>
                <MentorRosterView
                  mentorProfileData={mentorProfileData}
                  clinicId={clinicId}
                  clinicBrandId={clinicBrandId}
                  clinicName={thisClinicData?.title || thisClinicData?.name}
                />
              </Box>
            ) : (
              mentorProfileData && mentorClinicAssociationData && thisClinicData && (
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px',
                  width: '100%',
                }}
              >
                <CircularProgress />
              </Box>
            }
          >
            <DayViewCalendarComponent
              key={clinicId}
              forMentor={true}
              currentView={selectedTab}
              bookingLisitingApiSignature={`/api/secure/teacher/booked-slots/all-with-patient-info?get-patient-details=true`}
              publicBookingJoiningLinksApiSignature={`/api/secure/teacher/booked-slots/video-session-tokens/get`}
              sendPublicSessionLinkToPatientSignature="/api/secure/teacher/booked-slots/public-join-link/send"
              sendSecureSessionLinkToPatientSignature="/api/secure/teacher/booked-slots/secure-join-link/send"
                          pastPatientBookingsApiSignature="/api/secure/teacher/booked-event-history-of-a-patient"
              patientUpdateApiSignature="/api/secure/patient/update"
              patientTagsApiSignature="/api/secure/teacher/patient/tags"
              mentorAuthorisedToSaveAndGetPatientsUsingIdb={getAllRolesInBoolean().isDoctor}
              mentorId={mentorProfileData?.id}
              ContainerStyledComponent={PageMainContentContainer}
              mentorProfileData={mentorProfileData}
              thisClinicData={thisClinicData}
              headerContainerStyles={{ position: 'relative', top: '0px', marginTop: 0, paddingTop: 0, zIndex: 10 }}
              mentorClinicAssociationData={mentorClinicAssociationData}
              chambersData={mentorChamberRoomsData}
              meetingBookApiSignature="/api/secure/meeting/book"
              compactUI
              ref={dayViewCalendarRef}
              showValidateCheckInPopup
              emptySlotClickFunctionOverride={
                mentorProfileData?.teacherUnEditableFields?.canOnlyViewTheirOwnPatients
                  ? () => {}
                  : null
              }
              hideToggleSwitchForReportRelatedData={
                !mentorProfileData?.showReportsRelatedDataInBookingCalendar
              }
              // Patient search lives in the page header + the left-menu "Patients" item now, so it is
              // intentionally NOT passed here (which removes it from the calendar floating header).
              // LeftSideExtraComponent={<CheckInInformationComponent />}
              // RightSideExtraComponent={
              //   <CheckoutButtonComponent
              //     text="Checkout"
              //     styleProp={{
              //       width: '100px',
              //       flexDirection: 'column',
              //       fontSize: '12px',
              //       borderRadius: '0px',
              //       padding: 0,
              //       height: '100%',
              //     }}
              //     showExitButtonIfNotCheckedIn={true}
              //   />
              // }
              useClinicAppointmentTypes={false}
              clinicAppointmentTypesList={currentClinicEvents}
              vitalsFormGetApiSignature="/api/secure/practitioner/vitals/form"
              hidePatientContactDetails={hidePatientContactDetails}
              // Scroll-floating calendar header: clear the left-menu rail and drop the clinic
              // name/brand/time block (already shown in this page's main header).
              floatingHeaderLeftOffset={RAIL_WIDTH}
              hideFloatingHeaderClinicDetails
              // The New Appointment button already lives in the main header, so don't repeat it in
              // the calendar floating header.
              hideFloatingHeaderNewBooking
              // When the main header shows the day navigator, hide the calendar floating header's
              // own date picker to avoid two date controls.
              hideFloatingHeaderDatePicker={showDatePickerInMainHeader}
              // The 3-dots menu (refresh + slot duration) now lives in the filter strip, so the
              // floating header never renders its own. Filter pills + the "View all booked slots" (☰)
              // button are likewise gone. The floating header then collapses to nothing unless it's
              // still holding the date picker on a narrow screen.
              hideFloatingHeaderOverflowMenu
              hideFloatingHeaderFilterPills
              hideFloatingHeaderBookedSlotsButton
            />
          </Suspense>
              )
            )}
          </Box>
        </Box>
      </PageMainContentContainer>

      {/* Mobile left-menu drawer (desktop uses the persistent rail) */}
      <Drawer
        anchor="left"
        open={navOpen}
        onClose={() => setNavOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: 168,
            maxWidth: '70vw',
            padding: '12px 8px',
            backgroundColor: '#fff',
          },
        }}
      >
        <ClinicBookingsLeftMenu
          items={menuItems}
          selectedId={selectedTab}
          onSelect={handleMenuSelect}
          onOpenIntercom={handleOpenIntercom}
          onOpenDocSide={handleOpenDocSide}
        />
      </Drawer>

      {/* Patient Search Drawer */}
      <PatientSearchDrawer
        open={isPatientSearchOpen}
        showClinicalTabs
        onClose={() => setIsPatientSearchOpen(false)}
        onSelectPatient={handlePatientSelect}
        onBookAppointment={(patient) => {
          if (dayViewCalendarRef.current?.openBookingWithPatient) {
            dayViewCalendarRef.current.openBookingWithPatient(patient)
            setIsPatientSearchOpen(false)
          }
        }}
        clinicId={clinicId}
        clinicBrandId={clinicBrandId}
        mentorProfileData={mentorProfileData}
        searchPatientApiSignature="/api/secure/mentor/patient/list"
        createPatientApiSignature="/api/secure/patient/new"
        patientTagsApiSignature="/api/secure/teacher/patient/tags"
        canViewPatientHistory={true}
        patientHistoryGetApiSignature="/api/secure/patient/reports/list"
      />

      {/* Check-in / peek-in gate (blocking) + on-demand check-in screen (from the header
          chip while peeked in). Replaces the old redirect to the bare clinic list. */}
      <ClinicLocationCheckInGate
        open={showCheckInGate || manualGateOpen}
        variant={showCheckInGate ? 'gate' : 'manual'}
        onClose={() => setManualGateOpen(false)}
        clinicLocationData={thisClinicData}
        clinicBrandId={clinicBrandId}
        checkedInDetails={clinicAccess.checkedInDetails}
        activePeekIn={clinicAccess.activePeekIn}
      />

      {/* Selected Patient Booking Dialog */}
      {selectedPatient && (
        <Dialog
          open={!!selectedPatient}
          onClose={() => setSelectedPatient(null)}
          PaperComponent={PastTherapyReportDialogPaper}
          sx={{
            '@media (max-width: 768px)': {
              '& .MuiDialog-container': {
                alignItems: 'flex-start',
                padding: 0,
              },
              '& .MuiDialog-paper': {
                margin: 0,
                maxHeight: '100vh',
                height: '100vh',
                borderRadius: 0,
                display: 'flex',
                flexDirection: 'column',
              },
              '& .MuiDialogContent-root': {
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
              },
            },
          }}
        >
          <NewBookingPopup
            selectedPatient={selectedPatient}
            refreshData={refreshData}
            mentorsList={[]}
            appointmentTypesList={currentClinicEvents || []}
            closeDialog={() => setSelectedPatient(null)}
            patientHistoryGetApiSignature="/api/secure/teacher/booked-event-history-of-a-patient"
            hideSendToDoctorButton={false}
          />
        </Dialog>
      )}
    </AvailabilityPageContainer>
    </MedicalCertificateProvider>
  )
}
